import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocalization } from "@fluent/react";
import { Button, Dialog, Pagination } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import { useSearchParams } from "react-router-dom";

import { useUserFeatures } from "../../../../../components/contexts/UserFeaturesContext";
import BulkActionSnackbar from "../BulkActionSnackbar";
import EmptyState from "../EmptyState";
import MatchedReceiptDrawer from "../MatchedReceiptDrawer/MatchedReceiptDrawer";
import MatchedTable from "../MatchedTable";
import ReceiptDetailsDrawer from "../ReceiptDetailsDrawer/ReceiptDetailsDrawer";
import ReceiptsSortDropdown from "../ReceiptsSortDropdown/ReceiptsSortDropdown";
import ReceiptsTable from "../ReceiptsTable";
import SelectTransactionModal from "../SelectTransactionModal/SelectTransactionModal";
import { SelectionContext, SelectionProvider } from "../SelectionContext";
import UnmatchedReceiptDrawer from "../UnmatchedReceiptDrawer/UnmatchedReceiptDrawer";
import UnmatchedTable from "../UnmatchedTable";
import UploadReceiptDialog from "../UploadReceiptDialog/UploadReceiptDialog";
import { useAcceptMatch } from "../../hooks/useAcceptMatch";
import { useBulkAcceptMatches } from "../../hooks/useBulkAcceptMatches";
import { useBulkRejectMatches } from "../../hooks/useBulkRejectMatches";
import { useCanWriteReceipts } from "../../hooks/useCanWriteReceipts";
import {
  ReceiptBucket,
  RECEIPTS_DEFAULT_PAGE_SIZE,
  ReceiptSort,
  parseReceiptSort,
  useReceiptsList,
  useReceiptUploadPolling,
} from "../../hooks/useReceiptsList";
import { useRejectMatch } from "../../hooks/useRejectMatch";
import {
  transitionRowToUnmatched,
  useUnlinkMatch,
} from "../../hooks/useUnlinkMatch";

import styles from "./ReceiptInbox.module.scss";

import type { AcceptMatchError } from "../../hooks/useAcceptMatch";
import type { BulkRejectResponse } from "../../hooks/useBulkRejectMatches";
import type {
  ReceiptListRow,
  SuggestedMatch,
} from "../../hooks/useReceiptsList";
import type { RejectMatchError } from "../../hooks/useRejectMatch";
import type { UnlinkMatchError } from "../../hooks/useUnlinkMatch";

const BUCKETS = [
  ReceiptBucket.PENDING_REVIEW,
  ReceiptBucket.MATCHED,
  ReceiptBucket.UNMATCHED,
] as const;

const EMPTY_COPY: Record<
  ReceiptBucket,
  { heading: string; body: string; testId: string }
> = {
  [ReceiptBucket.PENDING_REVIEW]: {
    heading: "receipts-empty-pending-heading",
    body: "receipts-empty-pending-body",
    testId: "receipts-empty-state-pending",
  },
  [ReceiptBucket.MATCHED]: {
    heading: "receipts-empty-matched-heading",
    body: "receipts-empty-matched-body",
    testId: "receipts-empty-state-matched",
  },
  [ReceiptBucket.UNMATCHED]: {
    heading: "receipts-empty-unmatched-heading",
    body: "receipts-empty-unmatched-body",
    testId: "receipts-empty-state-unmatched",
  },
};

const SORT_QUERY_KEY = "sort";

const ZERO_OFFSETS: Record<ReceiptBucket, number> = {
  [ReceiptBucket.PENDING_REVIEW]: 0,
  [ReceiptBucket.MATCHED]: 0,
  [ReceiptBucket.UNMATCHED]: 0,
};

interface TabContentProps {
  rows: ReceiptListRow[];
  emptyHeading: string;
  emptyBody: string;
  emptyTestId: string;
  onRowClick?: (uuid: string) => void;
  onAcceptMatch?: (params: {
    suggestionId: number;
    receiptUuid: string;
  }) => void;
  onRejectMatch?: (params: {
    suggestionId: number;
    receiptUuid: string;
  }) => void;
  pendingAcceptSuggestionId?: number | null;
  pendingRejectSuggestionId?: number | null;
  showSelection?: boolean;
  bulkActionErrors?: Record<string, string>;
}

const TabContent = ({
  rows,
  emptyHeading,
  emptyBody,
  emptyTestId,
  onRowClick,
  onAcceptMatch,
  onRejectMatch,
  pendingAcceptSuggestionId,
  pendingRejectSuggestionId,
  showSelection,
  bulkActionErrors,
}: TabContentProps) => {
  if (rows.length === 0) {
    return (
      <EmptyState
        heading={emptyHeading}
        body={emptyBody}
        testId={emptyTestId}
      />
    );
  }
  return (
    <ReceiptsTable
      rows={rows}
      onRowClick={onRowClick}
      onAcceptMatch={onAcceptMatch}
      onRejectMatch={onRejectMatch}
      pendingAcceptSuggestionId={pendingAcceptSuggestionId}
      pendingRejectSuggestionId={pendingRejectSuggestionId}
      showSelection={showSelection}
      bulkActionErrors={bulkActionErrors}
    />
  );
};

export interface ReceiptInboxProps {
  bucket: ReceiptBucket;
  isUploadDialogOpen: boolean;
  onUploadDialogOpenChange: (isOpen: boolean) => void;
  /** Suppresses the built-in sort row for a host that renders its own `ReceiptsSortDropdown`. */
  hideSortRow?: boolean;
  /** A receipt uuid already resolved to `bucket` (e.g. a shared link) to open the matching drawer for. Re-applying the same value is a no-op. */
  deepLinkedUuid?: string | null;
  /** Notified whenever the open drawer's uuid changes — a row click (uuid) or a close (null) — so a host can mirror it in a shareable URL param. */
  onSelectedUuidChange?: (uuid: string | null) => void;
}

const ReceiptInboxContent = ({
  bucket,
  isUploadDialogOpen,
  onUploadDialogOpenChange,
  hideSortRow = false,
  deepLinkedUuid = null,
  onSelectedUuidChange,
}: ReceiptInboxProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { selectionState, setSelectionState } = useContext(SelectionContext);
  const canWrite = useCanWriteReceipts();
  const {
    receipts_manual_assign: receiptManualAssignEnabled = false,
    live_ledger: liveLedgerEnabled = false,
  } = useUserFeatures();
  const canManuallySelectTransaction = canWrite && receiptManualAssignEnabled;
  const showViewTransaction = receiptManualAssignEnabled && liveLedgerEnabled;
  const [pollForUuid, setPollForUuid] = useState<string | undefined>(undefined);
  const [selectedReceiptUuid, setSelectedReceiptUuid] = useState<string | null>(
    null
  );
  const [selectedMatchedUuid, setSelectedMatchedUuid] = useState<string | null>(
    null
  );
  // Captured at click time so the matched-transaction card survives list
  // refetches and tab changes that would otherwise drop the row out of scope.
  const [selectedMatchedTransaction, setSelectedMatchedTransaction] =
    useState<SuggestedMatch | null>(null);
  const [selectedMatchedRow, setSelectedMatchedRow] =
    useState<ReceiptListRow | null>(null);
  const [selectedUnmatchedUuid, setSelectedUnmatchedUuid] = useState<
    string | null
  >(null);
  const [unmatchedRowOverride, setUnmatchedRowOverride] =
    useState<ReceiptListRow | null>(null);
  const [pendingUnlink, setPendingUnlink] = useState<{
    // Null for a ledger attach, which unlinks by receipt uuid instead.
    suggestionId: number | null;
    receiptId: string;
  } | null>(null);
  const [selectTransactionTarget, setSelectTransactionTarget] = useState<{
    receiptId: string;
    suggestedTransactionId: string | null;
  } | null>(null);
  // Transient per-row error messages from the most recent bulk action,
  // keyed by receipt uuid. Cleared on tab switch and at the start of each
  // new bulk action.
  const [bulkActionErrors, setBulkActionErrors] = useState<
    Record<string, string>
  >({});
  // Captured at click time so onSuccess can advance the drawer even after the
  // optimistic removal has shrunk pendingReceiptUuids.
  const nextDrawerUuidRef = useRef<string | null | undefined>(undefined);
  // Seeds the drawer for a receipt already resolved to `bucket` by the host
  // (e.g. a shared link). Guarded to run once per uuid — handleSelectMatched
  // changes identity on every list refetch, and re-running it on refetch
  // would fight the "captured at click time" snapshot the matched-transaction
  // card relies on (see selectedMatchedTransaction above) every time it fires.
  // Reset to null on any close/transition so the same uuid can be
  // deep-linked open again.
  const lastAppliedDeepLinkRef = useRef<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const sort = parseReceiptSort(searchParams.get(SORT_QUERY_KEY));

  // Per-bucket offset state. Each bucket keeps its own page so switching
  // buckets and coming back preserves where the user was.
  const [offsetsByBucket, setOffsetsByBucket] =
    useState<Record<ReceiptBucket, number>>(ZERO_OFFSETS);
  const limit = RECEIPTS_DEFAULT_PAGE_SIZE;

  const handleSortChange = useCallback(
    (next: ReceiptSort) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next === ReceiptSort.MOST_RECENT) {
            updated.delete(SORT_QUERY_KEY);
          } else {
            updated.set(SORT_QUERY_KEY, next);
          }
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Keyed on the parsed `sort` value (not the change handler above) so a
  // sort change made by a host's own dropdown — see `hideSortRow` — resets
  // pagination too, not just the one driven by this component's own control.
  useEffect(() => {
    setOffsetsByBucket(ZERO_OFFSETS);
  }, [sort]);

  // One subscription per bucket so each tab owns its own page state and React
  // Query caches the data per [bucket, offset, limit]. All three fire on
  // mount; switching tabs is free.
  const pending = useReceiptsList({
    bucket: ReceiptBucket.PENDING_REVIEW,
    offset: offsetsByBucket[ReceiptBucket.PENDING_REVIEW],
    limit,
    sort,
  });
  const matched = useReceiptsList({
    bucket: ReceiptBucket.MATCHED,
    offset: offsetsByBucket[ReceiptBucket.MATCHED],
    limit,
    sort,
  });
  const unmatched = useReceiptsList({
    bucket: ReceiptBucket.UNMATCHED,
    offset: offsetsByBucket[ReceiptBucket.UNMATCHED],
    limit,
    sort,
  });
  const resultsByBucket = {
    [ReceiptBucket.PENDING_REVIEW]: pending,
    [ReceiptBucket.MATCHED]: matched,
    [ReceiptBucket.UNMATCHED]: unmatched,
  };
  const activeResult = resultsByBucket[bucket];
  const { receipts, total } = activeResult;
  const offset = offsetsByBucket[bucket];

  const handlePageChange = useCallback(
    (nextOffset: number) => {
      setOffsetsByBucket((prev) => ({ ...prev, [bucket]: nextOffset }));
    },
    [bucket]
  );

  // Removing rows from a bucket (e.g. bulk accept/reject on the last page) can
  // drop its total below that bucket's current offset, which would otherwise
  // strand the user on an empty page with the pagination control hidden. Clamp
  // every bucket's offset to its last valid page when its total shrinks.
  useEffect(() => {
    const totalsByBucket = {
      [ReceiptBucket.PENDING_REVIEW]: pending.total,
      [ReceiptBucket.MATCHED]: matched.total,
      [ReceiptBucket.UNMATCHED]: unmatched.total,
    };
    setOffsetsByBucket((prev) => {
      let changed = false;
      const next = { ...prev };
      BUCKETS.forEach((b) => {
        const maxOffset = Math.max(
          0,
          (Math.ceil(totalsByBucket[b] / limit) - 1) * limit
        );
        if (prev[b] > maxOffset) {
          changed = true;
          next[b] = maxOffset;
        }
      });
      return changed ? next : prev;
    });
  }, [pending.total, matched.total, unmatched.total, limit]);

  // Surface a list-fetch failure. The query uses retry:false and
  // keepPreviousData, so without this a failed page/sort/bucket request would
  // leave the prior page on screen with no indication anything went wrong.
  //
  // Track which bucket errors have already been notified to avoid duplicate
  // toasts when the user switches tabs and comes back to an errored tab.
  const notifiedErrorsRef = useRef<Set<string>>(new Set());
  const listError = activeResult.error;
  useEffect(() => {
    if (listError) {
      const errorKey = `${bucket}:${listError}`;
      if (!notifiedErrorsRef.current.has(errorKey)) {
        notifiedErrorsRef.current.add(errorKey);
        sendNotification({
          type: "negative",
          text: l10n.getString("receipts-list-error"),
        });
      }
    }
  }, [listError, bucket, l10n, sendNotification]);

  const {
    isVisible: isUploadedReceiptVisible,
    isExtractionFailed: isUploadedReceiptExtractionFailed,
  } = useReceiptUploadPolling(pollForUuid);

  // Ref-guard prevents the failure toast from firing more than once per
  // upload session (re-renders, query cache hits, etc.). Reset on each new
  // upload so a back-to-back failure still surfaces.
  const lastFailedToastUuidRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!pollForUuid) {
      lastFailedToastUuidRef.current = undefined;
      return;
    }
    if (!isUploadedReceiptVisible) return;
    if (
      isUploadedReceiptExtractionFailed &&
      lastFailedToastUuidRef.current !== pollForUuid
    ) {
      sendNotification({
        type: "negative",
        text: l10n.getString("receipts-upload-extraction-failed-toast"),
      });
      lastFailedToastUuidRef.current = pollForUuid;
    }
    setPollForUuid(undefined);
  }, [
    isUploadedReceiptVisible,
    isUploadedReceiptExtractionFailed,
    pollForUuid,
    sendNotification,
    l10n,
  ]);

  // Clear bulk selection (and any lingering bulk errors) when the user
  // switches buckets. Selections + errors are page-scoped and bucket-scoped —
  // keeping them across buckets would surface receipts that are no longer
  // visible.
  useEffect(() => {
    setSelectionState({});
    setBulkActionErrors({});
  }, [bucket, setSelectionState]);

  // After a refetch, drop any selected uuids that are no longer present in
  // the current page of results (e.g. a receipt was confirmed and moved to
  // another bucket). Mirror the same prune for bulk-action errors so chips
  // don't linger on rows that aren't visible anymore.
  //
  // Skip pruning on the render where `bucket` just changed — the clear-on-
  // bucket-switch effect above runs in the same pass and will wipe state to
  // {}. Pruning here against the prior bucket's `receipts` would otherwise
  // race the bucket-switch reset.
  const prevBucketRef = useRef(bucket);
  useEffect(() => {
    if (prevBucketRef.current !== bucket) {
      prevBucketRef.current = bucket;
      return;
    }
    const visibleUuids = new Set(receipts.map((row) => row.uuid));
    setSelectionState((previous) => {
      const next: typeof previous = {};
      let changed = false;
      Object.entries(previous).forEach(([uuid, row]) => {
        if (visibleUuids.has(uuid)) {
          next[uuid] = row;
        } else {
          changed = true;
        }
      });
      return changed ? next : previous;
    });
    setBulkActionErrors((previous) => {
      const next: Record<string, string> = {};
      let changed = false;
      Object.entries(previous).forEach(([uuid, msg]) => {
        if (visibleUuids.has(uuid)) {
          next[uuid] = msg;
        } else {
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [receipts, bucket, setSelectionState]);

  // Drawer prev/next navigates within the active bucket's visible rows. Each
  // list is populated only for its own bucket so a drawer never navigates a
  // stale list from a different bucket.
  const pendingReceiptUuids = useMemo(
    () =>
      bucket === ReceiptBucket.PENDING_REVIEW
        ? receipts.map((row) => row.uuid)
        : [],
    [bucket, receipts]
  );
  const matchedReceiptUuids = useMemo(
    () =>
      bucket === ReceiptBucket.MATCHED ? receipts.map((row) => row.uuid) : [],
    [bucket, receipts]
  );
  const unmatchedReceiptUuids = useMemo(
    () =>
      bucket === ReceiptBucket.UNMATCHED ? receipts.map((row) => row.uuid) : [],
    [bucket, receipts]
  );

  const acceptMatch = useAcceptMatch({
    onSuccess: () => {
      sendNotification({
        type: "success",
        text: l10n.getString("receipts-action-accept-success"),
      });
      if (nextDrawerUuidRef.current !== undefined) {
        setSelectedReceiptUuid(nextDrawerUuidRef.current);
        lastAppliedDeepLinkRef.current = null;
        onSelectedUuidChange?.(nextDrawerUuidRef.current);
        nextDrawerUuidRef.current = undefined;
      }
    },
    onError: (err: AcceptMatchError) => {
      sendNotification({
        type: "negative",
        text: l10n.getString(
          err.isConflict
            ? "receipts-action-accept-conflict-error"
            : "receipts-action-accept-error"
        ),
      });
      nextDrawerUuidRef.current = undefined;
    },
  });

  const rejectMatch = useRejectMatch({
    onSuccess: () => {
      // No success toast on reject (per reject-1.4 design). Only advance
      // the drawer if one was queued.
      if (nextDrawerUuidRef.current !== undefined) {
        setSelectedReceiptUuid(nextDrawerUuidRef.current);
        lastAppliedDeepLinkRef.current = null;
        onSelectedUuidChange?.(nextDrawerUuidRef.current);
        nextDrawerUuidRef.current = undefined;
      }
    },
    onError: (err: RejectMatchError) => {
      sendNotification({
        type: "negative",
        text: l10n.getString(
          err.isConflict
            ? "receipts-action-reject-conflict-error"
            : "receipts-action-reject-error"
        ),
      });
      nextDrawerUuidRef.current = undefined;
    },
  });

  const unlinkMatch = useUnlinkMatch({
    onSuccess: () => {
      if (selectedMatchedUuid === pendingUnlink?.receiptId) {
        setSelectedUnmatchedUuid(pendingUnlink.receiptId);
        setUnmatchedRowOverride(
          selectedMatchedRow
            ? transitionRowToUnmatched(selectedMatchedRow)
            : null
        );
        // The drawer swaps type but keeps showing the same receipt, so the
        // host's URL-backed receiptId doesn't change.
        onSelectedUuidChange?.(pendingUnlink.receiptId);
      } else {
        onSelectedUuidChange?.(null);
      }
      setSelectedMatchedUuid(null);
      setSelectedMatchedTransaction(null);
      setSelectedMatchedRow(null);
      setPendingUnlink(null);
      lastAppliedDeepLinkRef.current = null;
      sendNotification({
        type: "success",
        text: l10n.getString("receipts-action-unlink-success"),
      });
    },
    onError: (err: UnlinkMatchError) => {
      setPendingUnlink(null);
      sendNotification({
        type: "negative",
        text: l10n.getString(
          err.isConflict
            ? "receipts-action-unlink-conflict-error"
            : "receipts-action-unlink-error"
        ),
      });
    },
  });

  const handleSelectTransactionLinked = useCallback(() => {
    setSelectTransactionTarget(null);
    nextDrawerUuidRef.current = undefined;
    setSelectedReceiptUuid(null);
    setSelectedUnmatchedUuid(null);
    setUnmatchedRowOverride(null);
    lastAppliedDeepLinkRef.current = null;
    onSelectedUuidChange?.(null);
    sendNotification({
      type: "success",
      text: l10n.getString("receipts-action-accept-success"),
    });
  }, [sendNotification, l10n, onSelectedUuidChange]);

  const handleSelectTransactionLinkError = useCallback(() => {
    sendNotification({
      type: "negative",
      text: l10n.getString("receipts-select-transaction-error"),
    });
  }, [sendNotification, l10n]);

  const bulkAcceptMatches = useBulkAcceptMatches({
    onSuccess: (response, variables) => {
      const { summary, results } = response;
      // receiptUuidBySuggestionId was snapshotted at click time, so
      // failed-row chip mapping is unaffected if the refetch-prune effect
      // shrank selectionState while the request was in-flight.
      const errors: Record<string, string> = {};
      results.forEach((r) => {
        if (r.status === "failed") {
          const receiptUuid =
            variables.receiptUuidBySuggestionId[r.suggestion_id];
          if (receiptUuid) errors[receiptUuid] = r.error_message;
        }
      });
      setBulkActionErrors(errors);

      if (summary.failed === 0) {
        sendNotification({
          type: "success",
          text: l10n.getString("receipts-bulk-accept-success", {
            count: summary.succeeded,
          }),
        });
      } else {
        // Partial-failure copy doubles as the "all failed (server)" copy
        // — "0 accepted, N failed." is accurate and avoids conflating
        // per-item conflicts with a true request-level failure (handled
        // separately in onError).
        sendNotification({
          type: "negative",
          text: l10n.getString("receipts-bulk-accept-partial", {
            succeeded: summary.succeeded,
            failed: summary.failed,
          }),
        });
      }

      // Clear selections that successfully completed; the failed rows stay
      // selected so the user can retry without re-clicking.
      const succeededReceiptUuids = new Set(
        results.filter((r) => r.status === "success").map((r) => r.receipt_uuid)
      );
      setSelectionState((prev) => {
        const next: typeof prev = {};
        Object.entries(prev).forEach(([uuid, row]) => {
          if (!succeededReceiptUuids.has(uuid)) next[uuid] = row;
        });
        return next;
      });
    },
    onError: () => {
      // Request-level failure (network / 4xx on shape). Per-item state
      // unchanged; "Couldn't accept any matches" is the right copy here
      // because nothing actually got attempted server-side.
      sendNotification({
        type: "negative",
        text: l10n.getString("receipts-bulk-accept-error"),
      });
    },
  });

  const handleBulkAccept = useCallback(() => {
    const suggestionIds: number[] = [];
    const receiptUuidBySuggestionId: Record<number, string> = {};
    Object.values(selectionState).forEach((row) => {
      const sid = row.suggested_match?.id;
      if (sid != null) {
        suggestionIds.push(sid);
        receiptUuidBySuggestionId[sid] = row.uuid;
      }
    });
    if (suggestionIds.length === 0) return;
    setBulkActionErrors({});
    bulkAcceptMatches.mutate({ suggestionIds, receiptUuidBySuggestionId });
  }, [bulkAcceptMatches, selectionState]);

  const bulkRejectMatches = useBulkRejectMatches({
    onSuccess: (response: BulkRejectResponse, variables) => {
      const { summary, results } = response;
      const errors: Record<string, string> = {};
      results.forEach((r) => {
        if (r.status === "failed") {
          const receiptUuid =
            variables.receiptUuidBySuggestionId[r.suggestion_id];
          if (receiptUuid) errors[receiptUuid] = r.error_message;
        }
      });
      setBulkActionErrors(errors);

      if (summary.failed === 0) {
        sendNotification({
          type: "success",
          text: l10n.getString("receipts-bulk-reject-success", {
            count: summary.succeeded,
          }),
        });
      } else {
        // Partial-failure copy doubles as the "all failed (server)" copy
        // — "0 rejected, N failed." is accurate and avoids conflating
        // per-item conflicts with a true request-level failure (handled
        // separately in onError).
        sendNotification({
          type: "negative",
          text: l10n.getString("receipts-bulk-reject-partial", {
            succeeded: summary.succeeded,
            failed: summary.failed,
          }),
        });
      }

      const succeededReceiptUuids = new Set(
        results.filter((r) => r.status === "success").map((r) => r.receipt_uuid)
      );
      setSelectionState((prev) => {
        const next: typeof prev = {};
        Object.entries(prev).forEach(([uuid, row]) => {
          if (!succeededReceiptUuids.has(uuid)) next[uuid] = row;
        });
        return next;
      });
    },
    onError: () => {
      sendNotification({
        type: "negative",
        text: l10n.getString("receipts-bulk-reject-error"),
      });
    },
  });

  const handleBulkReject = useCallback(() => {
    const suggestionIds: number[] = [];
    const receiptUuidBySuggestionId: Record<number, string> = {};
    Object.values(selectionState).forEach((row) => {
      const sid = row.suggested_match?.id;
      if (sid != null) {
        suggestionIds.push(sid);
        receiptUuidBySuggestionId[sid] = row.uuid;
      }
    });
    if (suggestionIds.length === 0) return;
    setBulkActionErrors({});
    bulkRejectMatches.mutate({ suggestionIds, receiptUuidBySuggestionId });
  }, [bulkRejectMatches, selectionState]);

  const handleAcceptFromTable = useCallback(
    ({
      suggestionId,
      receiptUuid,
    }: {
      suggestionId: number;
      receiptUuid: string;
    }) => {
      acceptMatch.mutate({ suggestionId, receiptUuid });
    },
    [acceptMatch]
  );

  const handleRejectFromTable = useCallback(
    ({
      suggestionId,
      receiptUuid,
    }: {
      suggestionId: number;
      receiptUuid: string;
    }) => {
      rejectMatch.mutate({ suggestionId, receiptUuid });
    },
    [rejectMatch]
  );

  const snapshotNextDrawerUuid = useCallback(
    (receiptUuid: string) => {
      const currentIndex = pendingReceiptUuids.indexOf(receiptUuid);
      nextDrawerUuidRef.current =
        currentIndex >= 0 && currentIndex < pendingReceiptUuids.length - 1
          ? pendingReceiptUuids[currentIndex + 1]
          : null;
    },
    [pendingReceiptUuids]
  );

  const handleAcceptFromDrawer = useCallback(
    ({
      suggestionId,
      receiptUuid,
    }: {
      suggestionId: number;
      receiptUuid: string;
    }) => {
      snapshotNextDrawerUuid(receiptUuid);
      acceptMatch.mutate({ suggestionId, receiptUuid });
    },
    [acceptMatch, snapshotNextDrawerUuid]
  );

  const handleRejectFromDrawer = useCallback(
    ({
      suggestionId,
      receiptUuid,
    }: {
      suggestionId: number;
      receiptUuid: string;
    }) => {
      snapshotNextDrawerUuid(receiptUuid);
      rejectMatch.mutate({ suggestionId, receiptUuid });
    },
    [rejectMatch, snapshotNextDrawerUuid]
  );

  const pendingAcceptSuggestionId = useMemo<number | null>(() => {
    if (!acceptMatch.isPending) return null;
    const vars = acceptMatch.variables;
    if (typeof vars === "number") return vars;
    if (vars && typeof vars === "object" && "suggestionId" in vars) {
      return vars.suggestionId;
    }
    return null;
  }, [acceptMatch.isPending, acceptMatch.variables]);

  const pendingRejectSuggestionId = useMemo<number | null>(() => {
    if (!rejectMatch.isPending) return null;
    const vars = rejectMatch.variables;
    if (typeof vars === "number") return vars;
    if (vars && typeof vars === "object" && "suggestionId" in vars) {
      return vars.suggestionId;
    }
    return null;
  }, [rejectMatch.isPending, rejectMatch.variables]);

  const handleRequestUnlink = useCallback(
    ({
      suggestionId,
      receiptId,
    }: {
      suggestionId: number | null;
      receiptId: string;
    }) => {
      setPendingUnlink({ suggestionId, receiptId });
    },
    []
  );

  const handleCancelUnlink = useCallback(() => {
    // Gate dismiss on pending so the dialog can't disappear out from under an
    // in-flight mutation — the toast lands while the dialog is still on screen.
    if (unlinkMatch.isPending) return;
    setPendingUnlink(null);
  }, [unlinkMatch.isPending]);

  const handleConfirmUnlink = useCallback(() => {
    if (!pendingUnlink || unlinkMatch.isPending) return;
    unlinkMatch.mutate({
      suggestionId: pendingUnlink.suggestionId,
      receiptUuid: pendingUnlink.receiptId,
    });
  }, [pendingUnlink, unlinkMatch]);

  const handleManuallySelectTransaction = useCallback(
    (params: { receiptId: string; suggestedTransactionId: string | null }) => {
      setSelectTransactionTarget(params);
    },
    []
  );

  // The modal owns the link mutation and gates its own dismissal while the
  // request is in flight.
  const handleCloseSelectTransaction = useCallback(() => {
    setSelectTransactionTarget(null);
  }, []);

  const handleUploadSuccess = useCallback((uuid: string) => {
    setPollForUuid(uuid);
  }, []);
  const handleReceiptRemoved = useCallback(() => {
    // The removed receipt was already surfaced in the list (post-extraction);
    // refetch every bucket so it drops out and the stat-card counts update.
    pending.refetch();
    matched.refetch();
    unmatched.refetch();
  }, [pending, matched, unmatched]);
  const handleCloseDrawer = useCallback(() => {
    // Escape on the select-transaction modal also reaches the drawer (NDS
    // doesn't stop propagation) — only the topmost layer should close.
    if (selectTransactionTarget) return;
    // Cancel any pending auto-advance so an in-flight accept doesn't reopen
    // the drawer at the next pending uuid after the user explicitly closes it.
    nextDrawerUuidRef.current = undefined;
    setSelectedReceiptUuid(null);
    lastAppliedDeepLinkRef.current = null;
    onSelectedUuidChange?.(null);
  }, [selectTransactionTarget, onSelectedUuidChange]);
  const handleSelectReceipt = useCallback(
    (uuid: string) => {
      setSelectedReceiptUuid(uuid);
      setSelectedMatchedUuid(null);
      setSelectedMatchedTransaction(null);
      setSelectedMatchedRow(null);
      onSelectedUuidChange?.(uuid);
    },
    [onSelectedUuidChange]
  );
  const handleSelectMatched = useCallback(
    (uuid: string) => {
      const row = receipts.find((r) => r.uuid === uuid);
      setSelectedMatchedUuid(uuid);
      setSelectedMatchedTransaction(row?.suggested_match ?? null);
      setSelectedMatchedRow(row ?? null);
      setSelectedReceiptUuid(null);
      onSelectedUuidChange?.(uuid);
    },
    [receipts, onSelectedUuidChange]
  );
  const handleCloseMatchedDrawer = useCallback(() => {
    // When the unlink-confirmation Dialog is open, both it and the Drawer
    // receive the same window-level Escape keydown (NDS doesn't stop
    // propagation). Guard the drawer dismiss so Escape only closes the topmost
    // layer (the dialog) instead of tearing down both at once.
    if (pendingUnlink) return;
    setSelectedMatchedUuid(null);
    setSelectedMatchedTransaction(null);
    setSelectedMatchedRow(null);
    lastAppliedDeepLinkRef.current = null;
    onSelectedUuidChange?.(null);
  }, [pendingUnlink, onSelectedUuidChange]);

  const handleSelectUnmatched = useCallback(
    (uuid: string) => {
      setSelectedUnmatchedUuid(uuid);
      onSelectedUuidChange?.(uuid);
    },
    [onSelectedUuidChange]
  );
  const handleCloseUnmatchedDrawer = useCallback(() => {
    if (selectTransactionTarget) return;
    setSelectedUnmatchedUuid(null);
    setUnmatchedRowOverride(null);
    lastAppliedDeepLinkRef.current = null;
    onSelectedUuidChange?.(null);
  }, [selectTransactionTarget, onSelectedUuidChange]);

  useEffect(() => {
    if (!deepLinkedUuid || lastAppliedDeepLinkRef.current === deepLinkedUuid) {
      return;
    }
    lastAppliedDeepLinkRef.current = deepLinkedUuid;
    if (bucket === ReceiptBucket.PENDING_REVIEW) {
      handleSelectReceipt(deepLinkedUuid);
    } else if (bucket === ReceiptBucket.MATCHED) {
      handleSelectMatched(deepLinkedUuid);
    } else if (bucket === ReceiptBucket.UNMATCHED) {
      handleSelectUnmatched(deepLinkedUuid);
    }
  }, [
    deepLinkedUuid,
    bucket,
    handleSelectReceipt,
    handleSelectMatched,
    handleSelectUnmatched,
  ]);

  const selectedUnmatchedRow = useMemo(
    () =>
      selectedUnmatchedUuid
        ? unmatched.receipts.find(
            (row) => row.uuid === selectedUnmatchedUuid
          ) ??
          unmatchedRowOverride ??
          null
        : null,
    [unmatched.receipts, selectedUnmatchedUuid, unmatchedRowOverride]
  );

  const renderMatchedTab = () => {
    if (receipts.length === 0) {
      return (
        <EmptyState
          heading={l10n.getString("receipts-empty-matched-heading")}
          body={l10n.getString("receipts-empty-matched-body")}
          testId="receipts-empty-state-matched"
        />
      );
    }
    return (
      <MatchedTable
        rows={receipts}
        onRowClick={handleSelectMatched}
        onUnlink={canWrite ? handleRequestUnlink : undefined}
      />
    );
  };

  const renderUnmatchedTab = () => {
    if (receipts.length === 0) {
      return (
        <EmptyState
          heading={l10n.getString("receipts-empty-unmatched-heading")}
          body={l10n.getString("receipts-empty-unmatched-body")}
          testId="receipts-empty-state-unmatched"
        />
      );
    }
    return (
      <UnmatchedTable rows={receipts} onRowClick={handleSelectUnmatched} />
    );
  };

  const renderTabContent = () => {
    if (bucket === ReceiptBucket.MATCHED) return renderMatchedTab();
    if (bucket === ReceiptBucket.UNMATCHED) return renderUnmatchedTab();
    return (
      <TabContent
        rows={receipts}
        emptyHeading={l10n.getString(EMPTY_COPY[bucket].heading)}
        emptyBody={l10n.getString(EMPTY_COPY[bucket].body)}
        emptyTestId={EMPTY_COPY[bucket].testId}
        onRowClick={
          bucket === ReceiptBucket.PENDING_REVIEW
            ? handleSelectReceipt
            : undefined
        }
        onAcceptMatch={
          canWrite && bucket === ReceiptBucket.PENDING_REVIEW
            ? handleAcceptFromTable
            : undefined
        }
        onRejectMatch={
          canWrite && bucket === ReceiptBucket.PENDING_REVIEW
            ? handleRejectFromTable
            : undefined
        }
        pendingAcceptSuggestionId={pendingAcceptSuggestionId}
        pendingRejectSuggestionId={pendingRejectSuggestionId}
        showSelection={bucket === ReceiptBucket.PENDING_REVIEW}
        bulkActionErrors={bulkActionErrors}
      />
    );
  };

  return (
    <>
      <UploadReceiptDialog
        isOpen={isUploadDialogOpen}
        onClose={() => onUploadDialogOpenChange(false)}
        onUploadSuccess={handleUploadSuccess}
        onReceiptRemoved={handleReceiptRemoved}
      />
      <ReceiptDetailsDrawer
        uuid={selectedReceiptUuid}
        pendingUuids={pendingReceiptUuids}
        onClose={handleCloseDrawer}
        onSelect={handleSelectReceipt}
        onAcceptMatch={canWrite ? handleAcceptFromDrawer : undefined}
        onRejectMatch={canWrite ? handleRejectFromDrawer : undefined}
        onManuallySelectTransaction={
          canManuallySelectTransaction
            ? handleManuallySelectTransaction
            : undefined
        }
        showViewTransaction={showViewTransaction}
        pendingAcceptSuggestionId={pendingAcceptSuggestionId}
        pendingRejectSuggestionId={pendingRejectSuggestionId}
      />
      <MatchedReceiptDrawer
        uuid={selectedMatchedUuid}
        uuids={matchedReceiptUuids}
        onSelect={handleSelectMatched}
        matchedTransaction={selectedMatchedTransaction}
        suggestionId={selectedMatchedTransaction?.id ?? null}
        onUnlinkMatch={canWrite ? handleRequestUnlink : undefined}
        isUnlinkPending={unlinkMatch.isPending}
        onClose={handleCloseMatchedDrawer}
        showViewTransaction={showViewTransaction}
      />
      <UnmatchedReceiptDrawer
        uuid={selectedUnmatchedUuid}
        uuids={unmatchedReceiptUuids}
        onSelect={handleSelectUnmatched}
        row={selectedUnmatchedRow}
        onClose={handleCloseUnmatchedDrawer}
        onManuallySelectTransaction={
          canManuallySelectTransaction
            ? handleManuallySelectTransaction
            : undefined
        }
      />
      {selectTransactionTarget && (
        <SelectTransactionModal
          receiptId={selectTransactionTarget.receiptId}
          suggestedTransactionId={
            selectTransactionTarget.suggestedTransactionId
          }
          onClose={handleCloseSelectTransaction}
          onLinkSuccess={handleSelectTransactionLinked}
          onLinkError={handleSelectTransactionLinkError}
        />
      )}
      <Dialog
        isOpen={pendingUnlink !== null}
        onUserDismiss={handleCancelUnlink}
        title={l10n.getString("receipts-unlink-confirm-title")}
        footer={
          <div className={styles["unlink-confirm-footer"]}>
            <Button
              kind="secondary"
              label={l10n.getString("receipts-unlink-confirm-cancel")}
              onClick={handleCancelUnlink}
              disabled={unlinkMatch.isPending}
              testId="receipts-unlink-confirm-cancel"
            />
            <Button
              kind="primary"
              label={l10n.getString("receipts-unlink-confirm-confirm")}
              onClick={handleConfirmUnlink}
              isLoading={unlinkMatch.isPending}
              disabled={unlinkMatch.isPending}
              testId="receipts-unlink-confirm-confirm"
            />
          </div>
        }
        testId="receipts-unlink-confirm"
      >
        <p className="fontColor--primary">
          {l10n.getString("receipts-unlink-confirm-body")}
        </p>
      </Dialog>
      {!hideSortRow && total > 0 && (
        <div className={styles["sort-row"]}>
          <ReceiptsSortDropdown value={sort} onChange={handleSortChange} />
        </div>
      )}
      {renderTabContent()}
      {total > limit && (
        <div className={styles["pagination-row"]}>
          <Pagination
            testId="receipts-table-pagination"
            totalPages={Math.ceil(total / limit)}
            selectedPage={Math.floor(offset / limit) + 1}
            onPageChange={(page: number) =>
              handlePageChange((page - 1) * limit)
            }
          />
        </div>
      )}
      <BulkActionSnackbar
        onAccept={
          bucket === ReceiptBucket.PENDING_REVIEW ? handleBulkAccept : undefined
        }
        onReject={
          bucket === ReceiptBucket.PENDING_REVIEW ? handleBulkReject : undefined
        }
        isAccepting={bulkAcceptMatches.isPending}
        isRejecting={bulkRejectMatches.isPending}
      />
    </>
  );
};

const ReceiptInbox = (props: ReceiptInboxProps) => (
  <SelectionProvider>
    <ReceiptInboxContent {...props} />
  </SelectionProvider>
);

export default ReceiptInbox;
